import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@env/environment';
import {
  BookingStatusDisplay,
  BookingValidationStatus,
  ClaimGuestBookingApiResponse,
  ClaimGuestBookingResultDto,
  ClaimGuestMealResult,
  ConfirmMealClaimApiResponse,
  ConfirmMealClaimResult,
  GuestClaimResult,
  MealBooking,
  MealBookingReceiptDto,
  QRCodeValidationResultDto,
  ValidateQrApiResponse,
  ValidateQrResult,
} from '../models/redeem-booking.models';

const DEFAULT_MEAL_IMAGE =
  'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=120&h=120&fit=crop';

@Injectable({
  providedIn: 'root',
})
export class RedeemBookingService {
  private baseAPI = environment.baseAPI;

  constructor(private http: HttpClient) {}

  validateQrCode(qrCodePayload: string): Observable<ValidateQrResult> {
    const payload = qrCodePayload.trim();
    if (!payload) {
      return of({ booking: null, message: 'QR code or PIN is required.' });
    }

    return this.http
      .post<ValidateQrApiResponse>(`${this.baseAPI}/Vendors/validate-qr`, {
        qrCodePayload: payload,
      })
      .pipe(
        map((response) => {
          if (!response.status || !response.data) {
            return {
              booking: null,
              message: response.message || 'No booking found for this QR code or PIN.',
            };
          }

          return {
            booking: this.mapValidationResult(response.data, payload),
            message: response.message || 'Booking validated successfully.',
          };
        }),
        catchError((error) =>
          of({
            booking: null,
            message: this.extractApiMessage(
              error,
              'Unable to validate booking. Please try again.',
            ),
          }),
        ),
      );
  }

  confirmMealClaim(vendorId: string, claimPasscode: string): Observable<ConfirmMealClaimResult> {
    return this.http
      .post<ConfirmMealClaimApiResponse>(`${this.baseAPI}/Vendors/vendor/bookings/confirm`, {
        vendorId,
        claimPasscode: claimPasscode.trim(),
      })
      .pipe(
        map((response) => {
          if (!response.status || !response.data) {
            return {
              booking: null,
              message:
                response.message?.trim() || 'Unable to confirm booking. Please try again.',
            };
          }

          return {
            booking: this.mapReceipt(response.data),
            message: response.message?.trim() || 'Booking confirmed successfully.',
          };
        }),
        catchError((error) =>
          of({
            booking: null,
            message: this.extractApiMessage(
              error,
              'Unable to confirm booking. Please try again.',
            ),
          }),
        ),
      );
  }

  /** Redeems a guest's meal claim code. Single-shot — unlike the staff flow there is no
   *  separate "validate" endpoint, so a successful call has already redeemed the meal(s). */
  claimGuestMeal(
    claimCode: string,
    vendorId: string,
    performedByName?: string | null,
  ): Observable<ClaimGuestMealResult> {
    const code = claimCode.trim();
    if (!code) {
      return of({ claim: null, message: 'Guest code is required.' });
    }

    return this.http
      .post<ClaimGuestBookingApiResponse>(`${this.baseAPI}/GuestBookings/claim-meal`, {
        claimCode: code,
        vendorId,
        performedByName: performedByName?.trim() || null,
      })
      .pipe(
        map((response) => {
          if (!response.status || !response.data) {
            return {
              claim: null,
              message:
                response.message?.trim() || 'Unable to redeem guest meal. Please try again.',
            };
          }

          return {
            claim: this.mapGuestClaim(response.data),
            message: response.message?.trim() || 'Guest meal redeemed successfully.',
          };
        }),
        catchError((error) =>
          of({
            claim: null,
            message: this.extractApiMessage(
              error,
              'Unable to redeem guest meal. Please try again.',
            ),
          }),
        ),
      );
  }

  private mapGuestClaim(data: ClaimGuestBookingResultDto): GuestClaimResult {
    return {
      guestName: data.name?.trim() || 'Guest',
      email: data.email?.trim() || '',
      confirmedBy: data.confirmedBy?.trim() || '',
      numberOfMeals: data.numberOfMeals ?? data.meals?.length ?? 0,
      claimedAt: data.claimedAt,
      meals: (data.meals ?? []).map((meal) => ({
        name: meal.mealName?.trim() || 'Meal booking',
        reference: meal.bookingReference?.trim() || '',
      })),
    };
  }

  getStatusDisplay(status: BookingValidationStatus, redeemed: boolean): BookingStatusDisplay {
    if (redeemed || status === 'already_redeemed') {
      return {
        label: 'Already Redeemed',
        headerBg: 'bg-[#FEF3F2]',
        headerBorder: 'border-[#FECDCA]',
        cardBorder: 'border-[#FECDCA]',
        textClass: 'text-[#B42318]',
        iconType: 'x',
        servingTimeClass: 'text-[#B42318]',
        canConfirm: false,
        confirmButtonClass: 'bg-[#D0D5DD] text-white cursor-not-allowed',
      };
    }

    switch (status) {
      case 'valid':
        return {
          label: 'Booking Correct',
          headerBg: 'bg-[#ECFDF3]',
          headerBorder: 'border-[#ABEFC6]',
          cardBorder: 'border-[#ABEFC6]',
          textClass: 'text-[#027A48]',
          iconType: 'check',
          servingTimeClass: 'text-[#121212]',
          canConfirm: true,
          confirmButtonClass: 'bg-[#027A48] hover:bg-[#026939] text-white',
        };
      case 'expired':
        return {
          label: 'Booking Expired',
          headerBg: 'bg-[#FEF3F2]',
          headerBorder: 'border-[#FECDCA]',
          cardBorder: 'border-[#FECDCA]',
          textClass: 'text-[#B42318]',
          iconType: 'x',
          servingTimeClass: 'text-[#B42318]',
          canConfirm: false,
          confirmButtonClass: 'bg-[#D0D5DD] text-white cursor-not-allowed',
        };
      case 'invalid_cafeteria':
        return {
          label: 'Invalid Cafeteria',
          headerBg: 'bg-[#FEF3F2]',
          headerBorder: 'border-[#FECDCA]',
          cardBorder: 'border-[#FECDCA]',
          textClass: 'text-[#B42318]',
          iconType: 'x',
          servingTimeClass: 'text-[#B42318]',
          canConfirm: false,
          confirmButtonClass: 'bg-[#D0D5DD] text-white cursor-not-allowed',
        };
      case 'invalid_meal_time':
        return {
          label: 'Invalid Meal Time',
          headerBg: 'bg-[#FEF3F2]',
          headerBorder: 'border-[#FECDCA]',
          cardBorder: 'border-[#FECDCA]',
          textClass: 'text-[#B42318]',
          iconType: 'x',
          servingTimeClass: 'text-[#B42318]',
          canConfirm: false,
          confirmButtonClass: 'bg-[#D0D5DD] text-white cursor-not-allowed',
        };
      default:
        return {
          label: 'Invalid Booking',
          headerBg: 'bg-[#FEF3F2]',
          headerBorder: 'border-[#FECDCA]',
          cardBorder: 'border-[#FECDCA]',
          textClass: 'text-[#B42318]',
          iconType: 'x',
          servingTimeClass: 'text-[#B42318]',
          canConfirm: false,
          confirmButtonClass: 'bg-[#D0D5DD] text-white cursor-not-allowed',
        };
    }
  }

  private extractApiMessage(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      if (
        body &&
        typeof body === 'object' &&
        'message' in body &&
        typeof body.message === 'string' &&
        body.message.trim()
      ) {
        return body.message.trim();
      }
    }

    return fallback;
  }

  private mapValidationResult(
    data: QRCodeValidationResultDto,
    qrCodePayload: string,
  ): MealBooking {
    const staffName = data.bookingReference?.trim() || 'Guest';
    const validationStatus = this.mapValidationStatus(data.status);
    const redeemed = this.isRedeemedStatus(data.status);

    return {
      id: data.bookingId,
      pin: (data.claimPasscode ?? '').toUpperCase(),
      qrToken: qrCodePayload,
      staffName,
      staffInitials: this.toInitials(staffName),
      staffRole: 'Staff',
      mealName: data.mealName?.trim() || 'Meal booking',
      vendorName: '',
      mealImage: DEFAULT_MEAL_IMAGE,
      mealTypeTag: 'MEAL',
      dietaryTag: '',
      servingTime: this.formatDateTime(data.availableDate),
      cafeteria: '—',
      bookingDate: this.formatDate(data.availableDate),
      validationStatus,
      vendorId: '',
      redeemed,
      infoBanner: this.buildInfoBanner(validationStatus, data.status),
    };
  }

  private mapReceipt(data: MealBookingReceiptDto): MealBooking {
    const staffName = data.bookingReference?.trim() || 'Guest';
    const validationStatus: BookingValidationStatus = 'already_redeemed';

    return {
      id: data.bookingId,
      pin: (data.claimPasscode ?? '').toUpperCase(),
      qrToken: data.qrCodePayload ?? '',
      staffName,
      staffInitials: this.toInitials(staffName),
      staffRole: 'Staff',
      mealName: data.mealName?.trim() || 'Meal booking',
      vendorName: '',
      mealImage: DEFAULT_MEAL_IMAGE,
      mealTypeTag: 'MEAL',
      dietaryTag: '',
      servingTime: this.formatDateTime(data.availableDate),
      cafeteria: '—',
      bookingDate: this.formatDate(data.availableDate),
      validationStatus,
      vendorId: '',
      redeemed: true,
    };
  }

  private mapValidationStatus(status: string | null): BookingValidationStatus {
    const normalized = (status ?? '').toLowerCase();

    if (
      normalized.includes('redeem') ||
      normalized.includes('claimed') ||
      normalized.includes('confirmed')
    ) {
      return 'already_redeemed';
    }
    if (normalized.includes('expir')) {
      return 'expired';
    }
    if (normalized.includes('cafeteria') || normalized.includes('location')) {
      return 'invalid_cafeteria';
    }
    if (normalized.includes('time') || normalized.includes('window')) {
      return 'invalid_meal_time';
    }
    if (
      normalized.includes('valid') ||
      normalized.includes('pending') ||
      normalized.includes('booked') ||
      normalized.includes('active')
    ) {
      return 'valid';
    }

    return 'valid';
  }

  private isRedeemedStatus(status: string | null): boolean {
    const normalized = (status ?? '').toLowerCase();
    return (
      normalized.includes('redeem') ||
      normalized.includes('claimed') ||
      normalized.includes('confirmed')
    );
  }

  private buildInfoBanner(
    validationStatus: BookingValidationStatus,
    apiStatus: string | null,
  ): string | undefined {
    if (validationStatus === 'valid' || validationStatus === 'already_redeemed') {
      return undefined;
    }

    return apiStatus?.trim() || undefined;
  }

  private toInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  private formatDateTime(iso: string | undefined): string {
    if (!iso) return '—';

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private formatDate(iso: string | undefined): string {
    if (!iso) return '—';

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
}
